
import * as $ from 'jquery';

import * as AlternativeFormatsUtil from 'src/integration/alternativeFormatsUtil';
import * as IframeMessagingUtil from 'src/integration/iframeMessagingUtil';
import * as InstructorFeedbackUtil from 'src/integration/instructorFeedbackUtil';
import { Firefox96Plus } from 'src/integration/instructorFeedbackUtil';
import * as IntegrationTemplates from 'src/integration/templates/index';
import * as AllyConfig from 'src/shared/rest/Config';
import * as AllyFacade from 'src/shared/rest/facade';
import * as AllyUtil from 'src/shared/rest/util';
import { AllyAttributeMonitor } from 'src/shared/tinymce/AllyAttributeMonitor';

import { images } from './img';


const i18n = require('!!amdi18n-loader!src/integration/nls/integration');

// The attribute prefix for rich content attributes that a user has updated using Ally. These custom attributes will
// contain the value that the user has most recently set through an Ally operation
var ALLY_USER_UPDATED_ATTR_PREFIX = 'data-ally-user-updated';

// The attribute prefix for rich content attributes that Ally has applied a work-around for. For example, if a user
// has given alt text for an image in the course and there is another instance of that image elsewhere without an
// alt attribute, Ally can apply a "work around" by updating the `alt` attribute directly, and storing the original
// value in this associated "work around" attribute
var ALLY_WORK_AROUND_ATTR_PREFIX = 'data-ally-work-around';

export const Firefox96LearnClassic = Firefox96Plus && document.body.id === 'learn-oe-body';

/*********************
 * ALLY UTIL METHODS *
 *********************/

/**
 * Resolves language that is supported by integration in nls folder from language present in html/lang attribute.
 *
 * If the specific language is not supported:
 *  1. a shorter locale will be retried by splitting on `-` and popping the last value
 *     i.e. `sv-x-k12` eventually results in `sv`
 *  2. Default to `en-us-pro` if the final locale string is still not valid
 *
 * @param   {String}    htmlLanguage    The language from html/lang attribute for which to get
 *                                      the supported integration language
 * @return  {String}                    Supported integration language (integration/nls/{lang})
 */
export function resolveLanguage(htmlLanguage) {
    var lang;
    if (i18n[`__${ htmlLanguage}`]) {
        lang = htmlLanguage;
    } else {
        var parts = htmlLanguage.split('-');
        if (parts.length > 1) {
            parts.pop();
            lang = resolveLanguage(parts.join('-'));
        } else {
            lang = 'en-us-pro';
        }
    }
    return lang;
}

/**
 * Check whether the user is using a mobile or tablet device
 *
 * @return {Boolean} `true` if the user is using a mobile or tablet device
 */
export function isMobileOrTablet() {
    // A regex that checks for mobile and/or tablet browsers against the full user agent string
    // eslint-disable-next-line max-len
    var MOBILE_REGEX_FULL = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i;

    // A regex that checks for mobile and/or tablet browsers against the first 4 characters of the user agent
    // eslint-disable-next-line no-useless-escape,max-len
    var MOBILE_REGEX_INIT = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;

    var navigator = window.navigator;
    var userAgent = navigator.userAgent||navigator.vendor||window.opera;
    return MOBILE_REGEX_FULL.test(userAgent) || MOBILE_REGEX_INIT.test(userAgent.substr(0,4));
}

/**
 * Create a score indicator
 *
 * @param  {Object}    fileInfo                  The file information for which to create a score indicator
 * @param  {Object}    [options]                 Any extra options
 * @param  {Boolean}   [options.circle]          If `true`, uses the circular indicator icon, rather than the
 *                                               regular one. Defaults to `false`
 * @param  {Boolean}   [options.withFileType]    If `true`, the file type will be added into the label. Defaults to
 *                                               `false`
 * @return {jQuery}                              A score indicator wrapped into a jQuery function
 */
export function createScoreIndicator(fileInfo, options) {
    options = options || {
        'circle': false,
        'withFileType': false
    };

    const scoreInfo = getScoreInfo(fileInfo, options.withFileType);
    const iconType = (options.circle) ? 'circle' : 'default';
    const img = images.indicators[scoreInfo.range][iconType];

    var templateOptions = {
        'ALLY_DOMAIN': AllyConfig.baseUrl(),
        'fileId': fileInfo.id,
        'info': scoreInfo,
        'img': img
    };
    $.extend(templateOptions, options);

    return $(template('ally-accessibility-score-indicator', templateOptions));
}

/**
 * Resolve the alternative text of a file info object. If decorative, then the empty string is returned. Otherwise
 * we use the description as specified by the user, if any. If there is nothing to apply, `null` is strictly
 * returned.
 *
 * @param  {Object}    fileInfo    The file whose alternative text to resolve
 * @return {String}                The appropriate alt text content
 * @api private
 */
export function getAlternativeText(fileInfo) {
    if (fileInfo.decorative) {
        // Alternative text of the empty string indicates an image is decorative
        return '';
    }

    // If not decorative, use the user-specified description, and finally `null` if
    // there is none
    return fileInfo.description || null;
}

/**
 * Determine whether a file has been scored by Ally
 *
 * @param  {Object}     fileInfo    The file to check
 * @return {Boolean}                Whether or not the file is supported by Ally
 */
export function hasScore(fileInfo) {
    return (fileInfo && $.isNumeric(fileInfo.score));
}

/**
 * Determine whether a file has available alternative formats
 *
 * @param  {Object}  fileInfo     The file to check
 * @return {Boolean}              Whether or not a file has available alternative formats
 */
export function hasAvailableAlternativeFormats(fileInfo) {
    return (fileInfo && fileInfo.availableAlternativeFormats && fileInfo.availableAlternativeFormats.length > 0);
}

/**
 * Get the range and description of an accessibility score
 *
 * @param  {Object}    scoreData        The accessibility score for which to get the range and description
 * @param  {Boolean}   withFileType     Whether to include the file type in the title
 * @return {Object}                     Object with range and description properties. The range property will be
 *                                      `low` if the score is below 33%, `medium` if the score is between 33% and
 *                                      66% and `high` if the score is above 67%
 */
function getScoreInfo(scoreData, withFileType) {
    var score = scoreData.score;

    // Round the score down to two digits to prevent a different indicator to be shown because of precision.
    // e.g., a score of 0.659 getting an orange indicator but being displayed as 66%
    score = Math.floor(score * 100) / 100;
    var data = {};
    if (score <= 0.33) {
        data.range = 'low';
        data.alt = i18n.ACCESSIBILITY_SCORE_LOW;
    } else if (score <= 0.66) {
        data.range = 'medium';
        data.alt = i18n.ACCESSIBILITY_SCORE_MEDIUM;
    } else if (score === 1) {
        data.range = 'perfect';
        data.alt = i18n.ACCESSIBILITY_SCORE_PERFECT;
    } else {
        data.range = 'high';
        data.alt = i18n.ACCESSIBILITY_SCORE_HIGH;
    }

    if (withFileType) {
        // Concatenate the file type and title if required
        var fileType = i18n.FILE_TYPE_OTHER;
        if (scoreData.type === 'pdf') {
            fileType = i18n.FILE_TYPE_PDF;
        } else if (scoreData.type === 'image') {
            fileType = i18n.FILE_TYPE_IMAGE;
        } else if (scoreData.type === 'document') {
            fileType = i18n.FILE_TYPE_DOCUMENT;
        } else if (scoreData.type === 'presentation') {
            fileType = i18n.FILE_TYPE_PRESENTATION;
        } else if (scoreData.type === 'html-page') {
            fileType = i18n.FILE_TYPE_HTML_PAGE;
        }
        data.title = i18n.FILE_TYPE_SCORE
            .replace('{0}', fileType)
            .replace('{1}', data.alt);
    } else {
        // Re-use the alternative text as the tooltip title
        data.title = data.alt;
    }

    if (score != 1) {
        data.title += `\n${ i18n.CLICK_TO_IMPROVE}`;
    }

    return data;
}

/**
 * Add a seizure guard to an image
 *
 * @param {Object}      file    The Ally file report
 * @param {Object}      $img    The jQuery object representing the image that should be guarded
 */
export function addSeizureGuard(file, $img) {
    // Find any existing seizure guard for the image. A seizure guard will be a guard element that comes after
    // the image but with no other images in between
    var $seizureGuard = $img.nextAll(`.ally-image-seizure-guard[data-ally-file-id=${ file.id }]:first`);
    if ($seizureGuard.length > 0 && !$seizureGuard.prevAll('img:first').is($img)) {
        $seizureGuard = null;
    }

    if (!$seizureGuard || $seizureGuard.length === 0) {
        $seizureGuard = $(template('ally-image-seizure-guard', {
            'ALLY_DOMAIN': AllyConfig.baseUrl(),
            'fileId': file.id
        }));

        // When the button is clicked, the guard should self destruct
        $seizureGuard.find('button').click(function() {
            $seizureGuard.fadeOut();
            return false;
        });

        $img.after($seizureGuard);
        onCoordsChange($img, function(coords) {
            $seizureGuard.offset({
                'top': coords.top,
                'left': coords.left
            });
            $seizureGuard.width(coords.right - coords.left);
            $seizureGuard.height(coords.bottom - coords.top);
        });
    }
}

/**
 * Apply an update to an element attribute in user content that was done through Ally. This implies both an update
 * to the standard attribute and an update to an ally annotated version just to track the Ally modifications that
 * have been made to some degree.
 *
 * @param  {jQuery}     $el         The element to which to apply the update
 * @param  {String}     attrName    The name of the standard attribute (e.g., "alt")
 * @param  {String}     attrValue   The new value of the standard attribute. If unspecified, implies that the
 *                                  attribute should be removed
 */
export function applyAllyUserUpdate($el, attrName, attrValue) {
    // Change or remove the actual attribute
    if (attrValue === null || attrValue === undefined) {
        $el.removeAttr(attrName);
    } else {
        $el.attr(attrName, attrValue);
    }

    // Record the change
    var allyUserUpdateAttrName = getAllyUserUpdatedAttrName(attrName);
    if (attrValue === null || attrValue === undefined) {
        $el.removeAttr(allyUserUpdateAttrName);
    } else {
        $el.attr(allyUserUpdateAttrName, attrValue);
    }

    // Also reset any workaround we might have inserted for this attribute
    $el.removeAttr(getAllyWorkAroundAttrName(attrName));
}

/**
 * Determine if an image score indicator should be shown based on the file information provided. This does *not*
 * check if the user is a course manager, that should be done elsewhere.
 *
 * Images that are embedded in rich content items where there is no support for direct alt text updates have their
 * alt text value detached from the value we have stored in override_description on the Ally servers. Therefore, if
 * alt text is set directly on an image, the score indicator should be hidden, unless of course there are other
 * issues that should draw the instructor's attention.
 *
 * @param  {jQuery}         $img            The embedded image element to check
 * @param  {FileReference}  fileReference   The file reference that represents the image
 * @param  {Object}         fileInfo        The file info object
 * @return {Boolean}                        Whether or not the file should have a score indicator
 */
export function shouldShowImageIndicator($img, fileReference, fileInfo) {
    if (!hasScore(fileInfo)) {
        // If the file carries no score, the instructor feedback is disabled and we never want to show a score
        // indicator
        return false;
    } else if (fileInfo.seizureRisk) {
        // If the file has a risk for seizures, we always show the indicator
        return true;
    } else if (fileReference.isEmbedded()) {
        // If the file reference indicates that the image is an embedded reference, we always show the indicator.
        // This is mainly a proxy for determining if we support rich content updates in this functional area
        return true;
    }

    var workAroundSourceValues = getAllyWorkAroundSourceValues($img);
    if (Object.prototype.hasOwnProperty.call(workAroundSourceValues, 'alt')) {
        // If there was a workaround applied to alt, we know it's a bogus alt value, we can show the indicator
        return true;
    } else if ($img.attr('alt') !== '' && !AllyUtil.isAltTextMeaningful($img.attr('alt'), fileInfo.name)) {
        // If the alt text associated to the image is not meaningful (i.e., it was some bogus default like the file
        // name), show the indicator for the image
        return true;
    }

    // The user has input something meaningful into the alt text of the image, so we should supress the score
    // indicator
    return false;
}

/**
 * Apply a "work around" update to an element attribute in user content. This implies both an update to the standard
 * attribute described by `attrName`, as well as a custom "work around" attribute that records what the source/
 * original value was of this attribute. If a work around is applied multiple times, only the first "source value"
 * is kept.
 *
 * @param  {jQuery}     $el         The element to which to apply the work around
 * @param  {String}     attrName    The name of the standard attribute (e.g., "alt")
 * @param  {String}     attrValue   The new value of the standard attribute.
 *                                  If unspecified, implies that the attribute should be removed
 */
export function applyAllyWorkAround($el, attrName, attrValue) {
    var allyWorkAroundAttrName = getAllyWorkAroundAttrName(attrName);
    var previousAttrValue = JSON.stringify($el.attr(attrName)) || 'undefined';
    if (attrValue === null || attrValue === undefined) {
        $el.removeAttr(attrName);
    } else {
        $el.attr(attrName, attrValue);
    }

    // Only set the workaround metadata if it wasn't set already
    if (!$el.is(`[${ allyWorkAroundAttrName }]`)) {
        $el.attr(allyWorkAroundAttrName, previousAttrValue);
    }
}

/**
 * Get the source values of all the Ally work arounds that have been applied to this element.
 *
 * @param  {jQuery}     $el     The element from which to extract the source values
 * @return {Object}             An object keyed by standard attribute name (e.g., "alt") whose
 *                              values are the source values of those attributes before they were updated by Ally
 */
export function getAllyWorkAroundSourceValues($el) {
    var allyWorkArounds = {};
    var el = $el[0];
    if (el) {
        $.each(el.attributes, function(i, attr) {
            if (attr.name.indexOf(ALLY_WORK_AROUND_ATTR_PREFIX) === 0) {
                var attrName = attr.name.substring(ALLY_WORK_AROUND_ATTR_PREFIX.length + 1);
                allyWorkArounds[attrName] = getSourceAttrValue($el, attrName);
            }
        });
    }
    return allyWorkArounds;
}

/**
 * Get the value of the specified attribute that is located in the original source of the page. This value is what
 * was present before any work arounds were applied by Ally.
 *
 * @param  {jQuery}     $el         The element for which to get the source attribute value
 * @param  {String}     attrName    The name of the attribute whose source value to get
 */
export function getSourceAttrValue($el, attrName) {
    var allyWorkAroundAttrName = getAllyWorkAroundAttrName(attrName);
    if ($el.is(`[${ allyWorkAroundAttrName }]`)) {
        var allyWorkAroundAttrValueRaw = $el.attr(allyWorkAroundAttrName);
        if (allyWorkAroundAttrValueRaw === 'undefined') {
            return undefined;
        } else {
            return JSON.parse(allyWorkAroundAttrValueRaw);
        }
    } else {
        return $el.attr(attrName);
    }
}

/**
 * Get the value of the specified standard attribute name that has been updated through Ally, if any.
 *
 * For example, if 'alt' is provided as the `attrName` the attribute `data-ally-user-updated-alt` will be searched
 * for on the element.
 *
 * @param  {jQuery}     $el         The element whose attribute value to get
 * @param  {String}     attrName    The standard HTML attribute name
 * @return {String}                 The value of the attribute. The result will be `null` if there is no user
 *                                  updated value
 */
export function getAllyUserUpdatedValue($el, attrName) {
    var allyUserUpdatedAttrName = getAllyUserUpdatedAttrName(attrName);
    if ($el.is(`[${ allyUserUpdatedAttrName }]`)) {
        return $el.attr(allyUserUpdatedAttrName);
    } else {
        return null;
    }
}

/**
 * Get the query parameters from a given URL
 *
 * @param  {String}    url    The URL to get the query string parameters for
 * @return {Object}           The query string parameters in the given URL. If the URL contains no query string, an
 *                            empty object will be returned
 */
export function getQueryParameters(url) {
    var parameters = {};

    var querystring = url
        // Everything after first ?
        .split('?').slice(1).join('?')
        // Exclude fragment
        .split('#')[0]
        // Get each k=v pair
        .split('&');

    $.each(querystring, function(index, segment) {
        var hash = segment.split('=');
        parameters[decodeURIComponent(hash[0])] = decodeURIComponent(hash[1]);
    });

    return parameters;
}

/**
 * Run the given template w/ the given data making sure the i18n data is added.
 *
 * @see IntegrationTemplates
 *
 * @param  {String}     template    The id of the HTML template
 * @param  {Object}     [data]      The data that should be passed into the HTML template
 * @return {String}                 The rendered HTML template
 */
export function template(template, data) {
    data = data || {};
    data.i18n = i18n;

    return IntegrationTemplates.apply(template, data);
}

/**
 * Listen for the offset/size of a given element to change. Whenever it changes, invoke the given function.
 *
 * @param  {jQuery}     $el                     The element to watch
 * @param  {Function}   callback                The function that is invoked when the coords change
 * @param  {Object}     callback.coords         The new set of coords
 * @param  {Number}     callback.coords.top     The top offset of the element
 * @param  {Number}     callback.coords.right   The right offset of the element
 * @param  {Number}     callback.coords.bottom  The bottom offset of the element
 * @param  {Number}     callback.coords.left    The left offset of the element
 * @api private
 */
export function onCoordsChange($el, callback) {
    // Maintains the last known set of coords
    var lastCoords = {};

    // Maintains a handle to the interval timer, so it can be cleaned up when the element is removed
    var intervalHandle = null;

    /**
     * The function that is continuously run to determine if there was a change in coords
     */
    var _loop = function() {
        var offset = $el.offset();
        var width = $el.width();
        var height = $el.height();

        var currCoords = {
            'top': offset.top,
            'right': offset.left + width,
            'bottom': offset.top + height,
            'left': offset.left
        };

        // Only continue if the coordinates have changed. Otherwise we do nothing
        if (currCoords.top !== lastCoords.top || currCoords.right !== lastCoords.right ||
            currCoords.bottom !== lastCoords.bottom || currCoords.left !== lastCoords.left) {
            // Set the new set of coords
            lastCoords = currCoords;

            // First ensure the element is still on the DOM. If not, we're going to clean everything up here
            if (!$.contains(document.documentElement, $el[0])) {
                if (intervalHandle) {
                    clearInterval(intervalHandle);
                    intervalHandle = null;
                }
                return;
            }

            // Finally, run the callback
            return callback(lastCoords);
        }
    };

    // Start the interval timer
    intervalHandle = setInterval(_loop, 50);

    // Perform an immediate initial run
    _loop();
}

/**
 * Load an external CSS file
 *
 * @param  {String}         url             The URL of the external CSS file to load
 */
export function loadCSS(url) {
    $('<link/>', {
        'rel': 'stylesheet',
        'type': 'text/css',
        'href': url
    }).appendTo('head');
}

/**
 * Bind the Ally attribute monitor to the specified editor to support our custom attributes in TinyMCE.
 */
export function bindAllyAttributeMonitor(editorId) {
    return new AllyAttributeMonitor(editorId);
}

/**
 * Wait until a predecate is true. This function will keep polling until the predicate is true for
 * the first time
 *
 * @param  {Function}       predicate       The function that will return whether the predicate is true
 * @param  {Function}       callback        Standard callback function
 */
export function wait(predicate, callback) {
    predicate() ? callback() : setTimeout(function() {
        wait(predicate, callback);
    }, 50);
}

/**
 * Wait until a predecate is true. This function will keep polling until it reaches the specified number
 * of attempts
 *
 * @param  {Function}       predicate       The function that will return whether the predicate is true
 * @param  {Function}       success         Function to invoke when the predicate was fullfilled
 * @param  {Function}       error           Function to invoke when the predicate wasn't fullfilled
 * @param  {Number}         count           Number of attempts
 * @param  {Number}         interval        Interval between attempts
 */
export function waitUntil(predicate, success, error, count, interval) {
    error = error || function() {};
    count = count || 100;
    interval = interval || 50;
    predicate() ? success() : (!count ? error() : setTimeout(function() {
        waitUntil(predicate, success, error, count-1, interval);
    }, interval));
}

/**
 * Get a value for a given query string parameter. If the parameter could not be found, null will be returned.
 *
 * @param  {String}    name    The name of the query string parameter to get the value for
 * @return {String}            The value of the given query string parameter, or null if it could not be found
 */
export function getQueryStringParameter(name) {
    var re = new RegExp(`${name }=([^&]*?)(&|$)`);
    var match = re.exec(document.location.search);
    if (match && match.length >= 2) {
        return match[1];
    } else {
        return null;
    }
}

/**
 * Get the "user update attribute" name for the given standard attribute name.
 */
function getAllyUserUpdatedAttrName(attrName) {
    return `${ALLY_USER_UPDATED_ATTR_PREFIX }-${ attrName}`;
}

/**
 * Get the "work around attribute" name for the given standard attribute name.
 */
function getAllyWorkAroundAttrName(attrName) {
    return `${ALLY_WORK_AROUND_ATTR_PREFIX }-${ attrName}`;
}

/***********************
 * INSTRUCTOR FEEDBACK *
 ***********************/

/**
 * Launch the instructor feedback UI
 *
 * @param  {String} courseId The id of the course to which the file belongs
 * @param  {Object} fileReference The file reference for which we're launch instructor feedback
 * @param  {Object} [opts] Optional parameters
 * @param  {String} [opts.filePreviewUrl] The URL that can be used to show a preview of the file
 * @param  {Object} [opts.locator] If this content item is found in rich content, this locator provides a strategy
 *                                 to find the element inside the rich content body
 * @param  {String} [opts.locator.selector] The CSS selector for the element in the rich content body
 * @param  {Number} [opts.locator.index] The index of the element among all items selected with the
 *                                       `locator.selector` selector
 * @param  {String} [opts.richContentId] If this file is being referenced inside a rich content item, this should
 *                                       be set to the page id
 * @param  {Object} [opts.liveFeedback] The live in-progress feedback for the item, if any
 * @param  {Object} [callbacks] A set of optional callback functions that get invoked during or after the instructor
 *                              feedback
 * @param  {Object} [callbacks.closed] Invoked when the instructor feedback is closed
 * @param  {Function} [callbacks.deleted] Invoked when a file gets removed through the instructor feedback
 * @param  {String} [callbacks.deleted.fileId] The id of the file that was deleted
 * @param  {Function} [callbacks.updated] Invoked when a file gets updated
 * @param  {Object} [callbacks.updated.newFileInfo] The new file information
 * @param  {Function} [callbacks.replacedFile] Invoked when a file gets replaced
 * @param  {String} [callbacks.replacedFile.oldFileId] The id of the file that was replaced
 * @param  {Object} [callbacks.replacedFile.newFileInfo] The new file information
 * @param {Function} [callbacks.extractImageContext] Get surrounding context of the image
 */
export function launchInstructorFeedback(courseId, fileReference, opts, callbacks) {
    var locale = $('html').attr('lang');
    var iframeHtml = template('ally-instructor-feedback-iframe', {
        'ALLY_DOMAIN': AllyConfig.baseUrl(),
        'locale': locale
    });
    opts.locale = opts.locale || locale;
    InstructorFeedbackUtil.launch(
        AllyConfig.baseUrl(),
        null,
        AllyConfig.lmsType(),
        null,
        AllyConfig.clientId(),
        courseId,
        fileReference,
        iframeHtml,
        opts,
        callbacks
    );
}

export function updateLiveFeedbackContent(content) {
    InstructorFeedbackUtil.onLiveFeedbackContent(content);
}

export function updateLiveFeedback(liveFeedback) {
    InstructorFeedbackUtil.updateLiveFeedback(liveFeedback);
}

export function closeInstructorFeedback() {
    InstructorFeedbackUtil.closeInstructorFeedback();
}

/******************************
 * ACCESSIBLE VERSIONS IFRAME *
 ******************************/

/**
 * Launch the accessible versions
 *
 * @param {String} courseId The id of the course to which the file belongs
 * @param {String} contentId The arbitrary content id that the page correlates to
 * @param {Object} fileInfo The file information for which to show the accessible versions
 * @param {Object} [opts] Optional arguments
 * @param {Element} [opts.parentSelector] The parent element in which to embed the iframe
 * @param {Object[]} [opts.associatedFiles] A set of files associated with the rich content item
 *                                          for which alternative formats are requested
 * @param {String} [opts.content] The rich content body, if any. A SHA-1 hash will be taken to
 *                                prove user access to the content
 * @param {Object} [callbacks] A set of callbacks to be invoked when certain actions are performed
 *                             in and around the accessible versions modal
 * @param {Function} [callbacks.closed] Invoked when the accessible versions modal is closed
 */
export function launchAccessibleVersions(courseId, contentId, fileInfo, opts, callbacks) {
    opts = opts || {};
    var locale = $('html').attr('lang');
    var iframeHtml = template('ally-accessible-versions-iframe', {
        'ALLY_DOMAIN': AllyConfig.baseUrl(),
        'locale': locale,
        'platformName': AllyConfig.lmsType(),
        'platformUi': undefined
    });

    AlternativeFormatsUtil.launch(
        AllyConfig.baseUrl(),
        AllyConfig.lmsType(),
        null,
        AllyConfig.clientId(),
        courseId,
        contentId,
        fileInfo,
        iframeHtml,
        {
            'associatedFiles': opts.associatedFiles,
            'content': opts.content,
            'locale': locale,
            'parentSelector': opts.parentSelector
        },
        callbacks
    );
}

/**
 * Event listener that will be called when the Ally integration iFrame
 * wants to communicate with the Ally REST API
 *
 * The following event will be sent when the Ally integration iFrame
 * want to get/send data from/to the Ally REST API
 *
 * ```
 *    {
 *       'subject': 'http',
 *       'data': {
 *           <see facade.http>
 *       }
 *    }
 * ```
 *
 * The following event will be sent when the Ally integration iFrame
 * want to send a request of its own to the Ally REST API but needs the
 * authentication information
 *
 * ```
 *    {
 *       'subject': 'sign',
 *       'data': {
 *           <see facade.sign>
 *       }
 *    }
 * ```
 */
IframeMessagingUtil.listen('http', function(data, id, ev) {
    AllyFacade.getRestApi().http(data.method, data.path, data.parameters, data.opts, function(err, response) {
        var responseData = {
            'err': err,
            'response': response
        };
        IframeMessagingUtil.send(ev.source, 'http-response', responseData, id);
    });
});

IframeMessagingUtil.listen('sign', function(data, id, ev) {
    AllyFacade.getRestApi().sign(data.method, data.path, data.parameters, function(err, response) {
        var responseData = {
            'err': err,
            'response': response
        };
        IframeMessagingUtil.send(ev.source, 'sign-response', responseData, id);
    });
});

IframeMessagingUtil.listen('getDownloadUrl', function(data, id, ev) {
    AllyFacade.getRestApi().getDownloadUrl(data.parameters.fileId, function(err, downloadUrl) {
        var responseData = {
            'err': err,
            'response': downloadUrl
        };
        IframeMessagingUtil.send(ev.source, 'getDownloadUrl-response', responseData, id);
    });
});
